"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { FiCopy, FiCheck, FiDownload } from "react-icons/fi";
import { XIcon } from "./icons/XIcon";
import { FaXTwitter, FaTelegram } from "react-icons/fa6";
import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/hooks/useT";
import { STATIC_ASSETS } from "@/lib/cdn";
import BottomSheet from "@/components/ui/BottomSheet";
import { useUserProfile } from "@/contexts/UserProfileContext";

interface Position {
  outcome: string;
  outcomeIndex: number;
  shares: number;
  avgPrice: number;
  currentPrice: number;
  invested: number;
  currentValue: number;
  totalPnL: number;
}

interface SharePositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  position: Position | null;
  marketQuestion: string;
  marketImage?: string;
  marketSlug: string;
  username?: string;
  userAvatar?: string;
  source?: 'polymarket' | 'kalshi' | 'predict' | 'hyperliquid';
  /** When set, the position is closed — the card drops live Value/To-win/Now
   *  and shows the realized result instead. null/undefined = still open. */
  closedResult?: 'won' | 'lost' | 'sold' | null;
  /** Precio de venta (salida) para un SELL compartido desde Activity. Con esto
   *  el pill muestra "entrada¢ → salida¢" en vez de "@ $entrada". */
  exitPrice?: number;
}

export default function SharePositionModal({
  isOpen,
  onClose,
  position,
  marketQuestion,
  marketImage,
  marketSlug,
  username,
  userAvatar,
  source = 'polymarket',
  closedResult,
  exitPrice,
}: SharePositionModalProps) {
  const { user: privyUser, isReady: authReady } = useAuth();
  const { profile: profileCtx } = useUserProfile();
  const t = useT();

  // Get profile image from Privy social logins (Google, Twitter, Telegram, etc.)
  // Type assertions needed because Privy types don't include all runtime properties
  const getPrivyProfileImage = useCallback(() => {
    if (!privyUser) return null;
    return (privyUser.google as { profilePictureUrl?: string } | undefined)?.profilePictureUrl
      || (privyUser.twitter as { profilePictureUrl?: string } | undefined)?.profilePictureUrl
      || (privyUser.discord as { avatarUrl?: string } | undefined)?.avatarUrl
      || privyUser.farcaster?.pfp
      || (privyUser.telegram as { photo_url?: string } | undefined)?.photo_url
      || null;
  }, [privyUser]);

  // Get username from Privy social logins
  const getPrivyUsername = useCallback(() => {
    if (!privyUser) return null;
    return privyUser.twitter?.username
      || privyUser.farcaster?.username
      || privyUser.google?.name
      || privyUser.discord?.username
      || null;
  }, [privyUser]);

  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadingCard, setLoadingCard] = useState(true);
  const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);
  const [cardBlob, setCardBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState<string | undefined>(undefined);
  const [resolvedUsername, setResolvedUsername] = useState<string>("Trader");
  const [resolvedAvatar, setResolvedAvatar] = useState<string | undefined>(undefined);

  // Fetch user data AND generate card when modal opens
  // Combined into single effect to ensure user data is fetched before card generation
  useEffect(() => {
    if (!isOpen || !position) {
      // Reset state when modal closes
      setCardImageUrl(null);
      setCardBlob(null);
      setError(null);
      return;
    }

    // Wait for auth to be ready before generating card
    if (!authReady) {
      return;
    }

    let isCancelled = false;

    const fetchAndGenerateCard = async () => {
      setLoadingCard(true);
      setError(null);

      try {
        // Step 1: Get user profile from context, fetch referral code
        let fetchedUsername = "Trader";
        let fetchedAvatar: string | undefined = undefined;
        let fetchedReferralCode: string | undefined = undefined;

        // Use profile from context
        if (profileCtx?.user) {
          fetchedUsername = profileCtx.user.username || profileCtx.user.name || "Trader";
          fetchedAvatar = profileCtx.user.image || undefined;
        }

        try {
          const referralRes = await authFetch("/api/referral/code");  // Note: singular "referral", not "referrals"

          if (referralRes.ok) {
            const referralData = await referralRes.json();
            if (referralData.code?.code) {
              fetchedReferralCode = referralData.code.code;
            }
          }
        } catch (err) {
            // Continue with defaults
        }

        // Update state for potential re-renders
        setResolvedUsername(fetchedUsername);
        setResolvedAvatar(fetchedAvatar);
        setReferralCode(fetchedReferralCode);

        if (isCancelled) return;

        // Step 2: Generate the card with fetched data
        // Priority: Props (from parent profile fetch) > Internal fetch > Privy > Default
        const privyAvatar = getPrivyProfileImage();
        const privyUsername = getPrivyUsername();

        // Determine final username - props from parent are preferred (they come from profile fetch)
        let rawUsername = username && username !== "Trader"
          ? username
          : (fetchedUsername !== "Trader" ? fetchedUsername : (privyUsername || "Trader"));

        // Add @ prefix for real usernames (not "Trader")
        const finalUsername = rawUsername !== "Trader" ? `@${rawUsername.replace(/^@/, '')}` : rawUsername;

        // Determine final avatar URL - props from parent are preferred
        // Priority: Props > Internal fetch > Privy > CDN default
        const avatarCandidate = userAvatar || fetchedAvatar || privyAvatar;

        // Use avatar if it's a valid https URL, otherwise use CDN default
        const finalAvatar = avatarCandidate && avatarCandidate.startsWith('https://')
          ? avatarCandidate
          : STATIC_ASSETS.defaultAvatar;

        // Calculate P&L percent
        const pnlPercent = position.invested > 0
          ? (position.totalPnL / position.invested) * 100
          : 0;

        // Ensure prices are in valid range (0-1) like Telegram does
        const avgPrice = Math.min(1, Math.max(0, position.avgPrice || 0));
        // For a RESOLVED position the price is settled: won → $1, lost → $0.
        // Do NOT fall back to avgPrice when currentPrice is 0 — a lost market
        // has currentPrice 0, and `0 || avgPrice` would render it as break-even
        // (the bug that showed LOST cards at the entry price with +$0.00).
        const resolvedPrice = closedResult === 'won' ? 1 : closedResult === 'lost' ? 0 : null;
        const currentPrice = resolvedPrice ?? Math.min(1, Math.max(0, position.currentPrice ?? avgPrice));

        // Call backend to generate card (same format as Telegram)
        const response = await fetch('/api/pnl-card/custom/position', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: finalUsername,
            userAvatar: finalAvatar,
            marketQuestion,
            marketImage,
            outcome: position.outcome || (position.outcomeIndex === 0 ? 'YES' : 'NO'),
            avgPrice,
            currentPrice,
            size: position.shares || 0,
            // Mandar invested/currentValue explícitos: en cerradas size=0, así
            // que el fallback size*avgPrice del card daba "INVESTED $0.00". El
            // cost basis real vive en position.invested.
            invested: position.invested || 0,
            currentValue: position.currentValue || 0,
            // Precio de venta (solo SELL desde Activity) → pill "entrada → venta".
            ...(typeof exitPrice === 'number' ? { exitPrice: Math.min(1, Math.max(0, exitPrice)) } : {}),
            pnl: position.totalPnL || 0,
            pnlPercent: pnlPercent || 0,
            referralCode: fetchedReferralCode, // Include referral code for QR
            source, // Platform logo badge (polymarket/kalshi)
            // Closed positions: drop live Value/To-win/Now, show realized result.
            isClosed: !!closedResult,
            result: closedResult ?? undefined,
          }),
        });

        if (isCancelled) return;

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error('Failed to generate card');
        }

        // Get blob from response
        const blob = await response.blob();

        if (isCancelled) return;

        setCardBlob(blob);

        // Create object URL for display
        const url = URL.createObjectURL(blob);
        setCardImageUrl(url);
      } catch (err) {
        if (!isCancelled) {
          setError('Failed to generate card');
        }
      } finally {
        if (!isCancelled) {
          setLoadingCard(false);
        }
      }
    };

    fetchAndGenerateCard();

    // Cleanup
    return () => {
      isCancelled = true;
      if (cardImageUrl) {
        URL.revokeObjectURL(cardImageUrl);
      }
    };
    // Dep list deliberately uses primitive selectors instead of the
    // raw `position` object reference. The parent (`PositionsSection`)
    // builds a fresh `position` object literal on every render, and
    // `currentPrice` / `currentValue` / `totalPnL` update on every WS
    // price tick — so depending on `position` made the effect refire
    // multiple times per second, regenerating the 800×500 PNG on each
    // tick and visibly flickering the modal as the user watched.
    //
    // `currentPrice` is intentionally NOT in the deps: the share card
    // is a snapshot of the moment the user clicked Share — it shouldn't
    // chase live prices. avgPrice/invested/totalPnL are kept because
    // they shouldn't change during a single modal session, but if they
    // do (e.g. a sell) we want the card to reflect that.
    //
    // `getPrivyProfileImage` / `getPrivyUsername` are useCallback-bound
    // to `privyUser`; we read them via closure on first run and don't
    // need them to retrigger the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    authReady,
    position?.outcome,
    position?.outcomeIndex,
    position?.shares,
    position?.avgPrice,
    position?.invested,
    position?.totalPnL,
    marketQuestion,
    marketImage,
    username,
    userAvatar,
    profileCtx?.user?.username,
    profileCtx?.user?.image,
    source,
    closedResult,
    exitPrice,
  ]);

  const handleCopyImage = useCallback(async () => {
    if (!cardBlob) return;

    setCopying(true);
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": cardBlob,
        }),
      ]);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert("Could not copy to clipboard: " + (err as Error).message);
    } finally {
      setCopying(false);
    }
  }, [cardBlob]);

  const handleDownload = useCallback(() => {
    if (!cardBlob) return;

    const url = URL.createObjectURL(cardBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frenflow-${source}-position-${marketSlug}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [cardBlob, marketSlug]);

  const getShareText = useCallback(() => {
    if (!position) return "";

    const isYes = position.outcomeIndex === 0;
    const odds = (position.currentPrice * 100).toFixed(0);
    const toWin = position.shares;
    const pnlPercent = position.invested > 0
      ? ((position.totalPnL / position.invested) * 100).toFixed(0)
      : "0";

    return `I'm betting ${isYes ? "YES" : "NO"} on "${marketQuestion}" at ${odds}% odds

To win: $${toWin.toFixed(2)} ${Number(pnlPercent) >= 0 ? "+" : ""}${pnlPercent}%`;
  }, [position, marketQuestion]);

  const handleShareTwitter = useCallback(async () => {
    // First copy the image to clipboard
    if (cardBlob) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": cardBlob }),
        ]);
      } catch (err) {
      }
    }

    // Then open Twitter
    const text = getShareText() + "\n\nTrade on @frenflow_";
    const url = source === 'kalshi'
      ? `https://frenflow.com/event/kalshi/${marketSlug}?source=kalshi`
      : source === 'predict'
        ? `https://frenflow.com/predictfun/event/${marketSlug}`
        : source === 'hyperliquid'
          ? `https://frenflow.com/hyperliquid/event/${encodeURIComponent(marketSlug)}`
          : `https://frenflow.com/polymarket/event/${marketSlug}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, "_blank");

    // Show toast/alert to paste image
    setTimeout(() => {
      alert("Image copied! Paste it (Ctrl+V / Cmd+V) in your tweet to include the P&L card.");
    }, 500);
  }, [getShareText, marketSlug, cardBlob]);

  const handleShareTelegram = useCallback(async () => {
    // First copy the image to clipboard
    if (cardBlob) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": cardBlob }),
        ]);
      } catch (err) {
      }
    }

    const text = getShareText() + "\n\nTrade on FrenFlow";
    const url = source === 'kalshi'
      ? `https://frenflow.com/event/kalshi/${marketSlug}?source=kalshi`
      : source === 'predict'
        ? `https://frenflow.com/predictfun/event/${marketSlug}`
        : source === 'hyperliquid'
          ? `https://frenflow.com/hyperliquid/event/${encodeURIComponent(marketSlug)}`
          : `https://frenflow.com/polymarket/event/${marketSlug}`;
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    window.open(telegramUrl, "_blank");

    // Show toast/alert to paste image
    setTimeout(() => {
      alert("Image copied! Paste it (Ctrl+V / Cmd+V) in Telegram to include the P&L card.");
    }, 500);
  }, [getShareText, marketSlug, cardBlob]);

  if (!position) return null;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={t("action.shareAlpha")}
      zIndex={9999}
      className="md:max-w-2xl"
    >
      {/* Card Preview with 3D rotation */}
      <div className="p-4 md:p-6">
        {loadingCard ? (
          <div className="h-[320px] md:h-[480px] rounded-[5px] bg-[#0F0F12] flex flex-col items-center justify-center gap-3">
            <div className="w-8 h-8 border-2 border-[#CCFF00]/30 border-t-[#CCFF00] rounded-full animate-spin" />
            <p className="text-sm text-gray-400">Generating P&L card...</p>
          </div>
        ) : error ? (
          <div className="h-[320px] md:h-[480px] rounded-[5px] bg-[#0F0F12] flex flex-col items-center justify-center gap-3">
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={() => {
                setLoadingCard(true);
                setError(null);
              }}
              className="px-4 py-2 rounded-[5px] bg-[#CCFF00]/10 text-[#CCFF00] text-sm font-medium hover:bg-[#CCFF00]/20 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : cardImageUrl ? (
          <div className="relative rounded-[5px] overflow-hidden">
            <img
              src={cardImageUrl}
              alt="P&L Card"
              className="w-full h-auto rounded-[5px]"
              draggable={false}
            />
          </div>
        ) : null}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 px-4 pb-4 md:px-6 md:pb-6">
        {/* Copy Image */}
        <button
          onClick={handleCopyImage}
          disabled={copying || !cardBlob}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[5px] border border-gray-200 dark:border-[#25252B] bg-gray-50 dark:bg-[#0F0F12] hover:bg-gray-100 dark:hover:bg-[#25252B] transition-colors text-xs font-semibold text-gray-900 dark:text-white disabled:opacity-50"
        >
          {copied ? (
            <>
              <FiCheck className="w-4 h-4 text-[#00FF9D]" />
              Copied!
            </>
          ) : (
            <>
              <FiCopy className="w-4 h-4" />
              Copy
            </>
          )}
        </button>

        {/* Download */}
        <button
          onClick={handleDownload}
          disabled={!cardBlob}
          className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[5px] border border-gray-200 dark:border-[#25252B] bg-gray-50 dark:bg-[#0F0F12] hover:bg-gray-100 dark:hover:bg-[#25252B] transition-colors text-xs font-semibold text-gray-900 dark:text-white disabled:opacity-50"
        >
          <FiDownload className="w-4 h-4" />
        </button>

        {/* Share on X */}
        <button
          onClick={handleShareTwitter}
          disabled={!cardBlob}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[5px] bg-black border border-gray-700 hover:border-gray-500 transition-colors text-xs font-semibold text-white disabled:opacity-50"
        >
          <FaXTwitter className="w-4 h-4" />
          X
        </button>

        {/* Telegram */}
        <button
          onClick={handleShareTelegram}
          disabled={!cardBlob}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[5px] bg-[#0088cc] hover:bg-[#0077b5] transition-colors text-xs font-semibold text-white disabled:opacity-50"
        >
          <FaTelegram className="w-4 h-4" />
          Telegram
        </button>
      </div>
    </BottomSheet>
  );
}
